import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { TrendingUp, Cancel, CheckCircle } from '@mui/icons-material';

interface UpgradeConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: {
    name: string;
    amount: number;
  };
  newPlan: {
    name: string;
    amount: number;
  };
  loading?: boolean;
}

const UpgradeConfirmationModal: React.FC<UpgradeConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentPlan,
  newPlan,
  loading = false
}) => {
  const upgradeValue = newPlan.amount - currentPlan.amount;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingUp color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Upgrade de Plano Disponível
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Você já possui uma assinatura ativa. Deseja fazer upgrade para um plano superior?
          </Typography>
        </Alert>

        <Stack spacing={2}>
          {/* Plano Atual */}
          <Box 
            sx={{ 
              p: 2, 
              border: '1px solid #e0e0e0', 
              borderRadius: 1,
              backgroundColor: '#f5f5f5'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" color="text.secondary">
                Plano Atual
              </Typography>
              <Chip 
                label="Ativo" 
                size="small" 
                color="success" 
                icon={<CheckCircle />}
              />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              {currentPlan.name}
            </Typography>
            <Typography variant="body1" color="primary" fontWeight="bold">
              R$ {currentPlan.amount.toFixed(2)}/mês
            </Typography>
          </Box>

          {/* Seta de Upgrade */}
          <Box display="flex" justifyContent="center">
            <TrendingUp 
              sx={{ 
                fontSize: 32, 
                color: 'primary.main',
                transform: 'rotate(90deg)'
              }} 
            />
          </Box>

          {/* Novo Plano */}
          <Box 
            sx={{ 
              p: 2, 
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              backgroundColor: 'primary.50',
              position: 'relative'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" color="primary">
                Novo Plano
              </Typography>
              <Chip 
                label="UPGRADE" 
                size="small" 
                color="primary" 
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {newPlan.name}
            </Typography>
            <Typography variant="body1" color="primary" fontWeight="bold">
              R$ {newPlan.amount.toFixed(2)}/mês
            </Typography>
          </Box>

          {/* Diferença de Valor */}
          <Box 
            sx={{ 
              p: 2, 
              backgroundColor: 'success.50',
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Diferença mensal
            </Typography>
            <Typography variant="h6" color="success.main" fontWeight="bold">
              + R$ {upgradeValue.toFixed(2)}
            </Typography>
          </Box>
        </Stack>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Atenção:</strong> Sua assinatura atual será cancelada e a nova começará imediatamente. 
            A próxima cobrança será no valor do novo plano.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          startIcon={<TrendingUp />}
          disabled={loading}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Processando...' : 'Confirmar Upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeConfirmationModal; 